import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { navigationService, toastNotificationsService } from 'src/config/rootService';
import { Dispatch } from 'src/config/rootStore';
import { CoachingVisitNode } from 'src/generated/webapp_gql';
import { ProviderType } from 'src/modules/user/models/user';
import { convertSessionScheduledTimeToStartAndEndTime } from 'src/modules/videoSessions/helpers/sessionSlotToReadableDate';
import { colors as dsColors, mediaQuery } from '@modernhealth/vital-foundation';

import {
    ButtonGroup,
    Button,
    BodyTextMedium,
    HeadingText40,
    HeadingText60,
    pxToRem,
} from '@modernhealth/vital-components';

import { FormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';

import { useCancelCoachingSessionMutation } from '../documents/cancelCoachingSessionMutation.generated';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface SessionDetailsModalContentProps {
    session: CoachingVisitNode;
    onClose: () => void;
}

export const SessionDetails: FC<SessionDetailsModalContentProps> = ({ session, onClose }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [isCancelConfirmationVisible, setCancelConfirmationVisible] = useState(false);

    const { videoSessions, user } = useDispatch<Dispatch>();

    const [cancelSession, { loading: isCancellingSession }] = useCancelCoachingSessionMutation({
        client: appClient,
    });

    const providerType = session.coachAssignment?.coach?.providerType || ProviderType.COACH;

    const { pammMemberScheduling } = useFlags();

    const getSessionTypeDescription = () => {
        const sessionTypeDescriptions: { [key: string]: string } = {
            [ProviderType.THERAPIST]: 'videoSessions:session_details.title.therapy',
            [ProviderType.COACH]: 'videoSessions:session_details.title.coaching',
            [ProviderType.PSYCHIATRIST]: `videoSessions:session_details.title.${
                session.isPsychiatryInitialEvaluation
                    ? 'psychiatry_initial_evaluation'
                    : 'psychiatry_follow_up'
            }`,
        };

        return t(sessionTypeDescriptions[providerType], {
            name: provider?.firstName,
        });
    };

    const handleSessionCancel = async () => {
        setLoading(true);

        try {
            const response = await cancelSession({ variables: { sessionUuid: session.uuid } });
            const ok = response.data?.cancelEvent?.ok;

            if (!ok) {
                throw new Error();
            }

            await videoSessions.fetchSessions();
            // Re-fetch user info since we're getting session utilization info from `me` query
            await user.fetchUserInfo();

            toastNotificationsService.renderSuccessToast(config.messages.sessionCanceled);
        } catch (err) {
            toastNotificationsService.renderErrorToast(config.messages.genericError);
        } finally {
            setLoading(false);
            onClose();
        }
    };

    const handleSessionJoin = () => navigationService.navigate(`/video-session/${session.uuid}`);

    const provider = session.coachAssignment?.coach;
    const { scheduledEndTime, scheduledStartTime } =
        convertSessionScheduledTimeToStartAndEndTime(session);

    if (isCancelConfirmationVisible) {
        return (
            <div>
                <CancelConfirmationContent>
                    <HeadingText40 centered>
                        {t('videoSessions:session_details.cancel_confirmation.title')}
                    </HeadingText40>
                    <BodyTextMedium fontColor="gray65" centered>
                        <Trans
                            t={t}
                            ns="videoSessions"
                            i18nKey="session_details.cancel_confirmation.description"
                            components={{
                                strong: (
                                    <HightlightText
                                        onClick={() =>
                                            window.open(
                                                'https://support.modernhealth.com/hc/en-us/articles/360001419846-Cancellation-Policy',
                                                '_blank',
                                            )
                                        }
                                    />
                                ),
                            }}
                        />
                    </BodyTextMedium>
                </CancelConfirmationContent>

                <CancelConfirmationButtonGroup>
                    <ButtonGroup>
                        <Button
                            variant="secondary"
                            busy={isCancellingSession}
                            onClick={() => setCancelConfirmationVisible(false)}
                        >
                            {t('common.back')}
                        </Button>
                        <Button
                            variant="destructive"
                            busy={isCancellingSession}
                            disabled={isCancellingSession}
                            onClick={handleSessionCancel}
                        >
                            {t('videoSessions:session_details.cancel')}
                        </Button>
                    </ButtonGroup>
                </CancelConfirmationButtonGroup>
            </div>
        );
    }

    return (
        <Container>
            <ProviderImageContainer>
                {provider?.avatar ? (
                    <ProviderImage src={provider.avatar} />
                ) : (
                    <HeadingText60 marginBottom={0}>{`${provider?.firstName?.[0] ?? ''}${
                        provider?.lastName?.[0] ?? ''
                    }
                `}</HeadingText60>
                )}
            </ProviderImageContainer>
            <HeadingText40 marginBottom={10} centered>
                {getSessionTypeDescription()}
            </HeadingText40>
            <BodyTextMedium fontColor="gray65" marginBottom={32} centered>
                <FormattedSessionDateTimeRange
                    start={scheduledStartTime.toDate()}
                    end={scheduledEndTime.toDate()}
                    month="short"
                />
            </BodyTextMedium>

            <ButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.back')}
                </Button>
                <Button variant="primary" onClick={handleSessionJoin}>
                    {t('videoSessions:session_details.join')}
                </Button>
            </ButtonGroup>
            {/* todo: remove this validation once we enable the cancelling flow for PAMM */}
            {(providerType !== ProviderType.PSYCHIATRIST || pammMemberScheduling) && (
                <CancelSessionButton>
                    <Button
                        fluid
                        variant="flat"
                        size="large"
                        busy={loading}
                        onClick={() => setCancelConfirmationVisible(true)}
                    >
                        {t('videoSessions:session_details.cancel')}
                    </Button>
                </CancelSessionButton>
            )}
        </Container>
    );
};

const Container = styled.div({
    paddingTop: 35,
    display: 'flex',
    flexDirection: 'column',
});

const ProviderImageContainer = styled.div({
    width: pxToRem(98),
    height: pxToRem(98),
    borderRadius: '50%',
    alignSelf: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    display: 'flex',
    [mediaQuery.maxWidth('md')]: {
        width: pxToRem(40),
        height: pxToRem(40),
    },
    marginBottom: pxToRem(22),
    backgroundColor: dsColors.violet20,
});

const ProviderImage = styled.img({
    width: '110%',
    height: '110%',
});

const CancelConfirmationContent = styled.div({
    maxWidth: '80%',
    margin: '0 auto',
});

const HightlightText = styled.span({
    fontWeight: 'bold',
    textDecoration: 'underline',
    cursor: 'pointer',
});

const CancelSessionButton = styled.div`
    margin-top: ${pxToRem(10)};

    button {
        &,
        &:hover,
        &:focus {
            color: ${dsColors.red40};
        }
    }
`;
const CancelConfirmationButtonGroup = styled.div`
    margin-top: ${pxToRem(32)};
`;
